/**
 * Finds a client the advisor already has that a new invitation would duplicate.
 *
 * "Invite Client" always creates a client. An advisor who made a client with Quick Start and then
 * came here to email them the intake form ended up with two records for one person, and the
 * finished intake attached to the second one (UX walkthrough, 9 Oct 2026). The invite flow asks
 * before doing that, and this is how it decides there is something to ask about.
 *
 * A match is the same email address, or the same first and last name. Email wins when both
 * exist, because two people can share a name. A name only counts when both parts were typed:
 * "Jane" on its own matches too many people to be worth interrupting for.
 *
 * Only the advisor's own clients are searched. The dashboard list also holds the clients of the
 * advisors they supervise and the sample clients, and offering to email one of those would send
 * the intake to somebody else's client.
 */

import { isPlaceholderEmail } from '@shared';

interface InviteIdentity {
  firstName: string;
  lastName: string;
  email: string;
}

const normalize = (value: string | null | undefined) => (value ?? '').trim().toLowerCase();

function mostRecentlyAdded(clients: Client[]): Client | null {
  return clients.reduce<Client | null>((latest, client) => {
    if (!latest) return client;
    return new Date(client.clientAddedDateTime) > new Date(latest.clientAddedDateTime)
      ? client
      : latest;
  }, null);
}

export function findExistingClient(
  clients: Client[],
  invite: InviteIdentity,
  advisorId: string,
): Client | null {
  const email = normalize(invite.email);
  const firstName = normalize(invite.firstName);
  const lastName = normalize(invite.lastName);
  const ownClients = clients.filter(
    client => client.advisorId === advisorId && !client.isSampleClient,
  );

  if (email) {
    const sameEmail = ownClients.filter(
      client => !isPlaceholderEmail(client.clientEmail) && normalize(client.clientEmail) === email,
    );
    if (sameEmail.length > 0) return mostRecentlyAdded(sameEmail);
  }

  if (!firstName || !lastName) return null;

  return mostRecentlyAdded(
    ownClients.filter(
      client =>
        normalize(client.clientFirstName) === firstName &&
        normalize(client.clientLastName) === lastName,
    ),
  );
}
